import type { LibrarySymbolInfo } from "@public/perps/charting_library"
import atomIcon from "@perps/market/image/atom-logo.svg"
import btcIcon from "@perps/market/image/btc-logo.svg"
import ethIcon from "@perps/market/image/eth-logo.svg"
import bnbIcon from "@perps/market/image/bnb-logo.svg"
import dogeIcon from "@perps/market/image/doge-logo.svg"
import runeIcon from "@perps/market/image/rune-logo.svg"
import xrpIcon from "@perps/market/image/xrp-logo.svg"
import avaxIcon from "@perps/market/image/avax-logo.svg"
import usdtIcon from "@perps/market/image/usdt-logo.svg"
import usdcIcon from "@perps/market/image/usdc-logo.svg"
import bchIcon from "@perps/market/image/bch-logo.svg"
import ltcIcon from "@perps/market/image/ltc-logo.svg"
import solIcon from "@perps/market/image/sol-logo.svg"
import trxIcon from "@perps/market/image/trx-logo.svg"
import type { MarketId, MarketType } from "@perps/sdk/types"

import { MarketDisplayConfigMap } from "./marketDisplayConfig"

/**
 * How the market is displayed in URLs
 */
export class MarketSlug {
  slug: string
  private constructor(slug: string) {
    this.slug = slug
  }

  static get(slug: string): MarketSlug {
    const ret = MARKET_SLUGS.get(slug)
    if (ret !== undefined) {
      return ret
    }
    const newSlug = new MarketSlug(slug)
    MARKET_SLUGS.set(slug, newSlug)
    return newSlug
  }

  toString(): string {
    return this.slug
  }

  toJSON(): string {
    return this.slug
  }
}

const MARKET_SLUGS: Map<string, MarketSlug> = new Map()

export interface MarketDisplayConfig {
  icon: string
  collateralIcon: string
  benchmarksSymbol: string | { id: string; inverted: boolean }[]
  makeTradingViewSymbolInfo: () => LibrarySymbolInfo
  slug: MarketSlug
  id: MarketId
  rujiraId: string
  base: string
  quote: string
  collateral: string
  notional: string
  type: MarketType
  /**
   * Used for display within the UI
   */
  title: string
  /**
   * Title used on the earn page list specifically
   */
  earnPageTitle: string
  /**
   * Override the number of quote decimal places
   *
   * Helpful for collateral is quote markets which have a small USD value
   */
  quoteDecimalPlaces?: number
}

export const marketDisplayConfigMap = new MarketDisplayConfigMap([
  {
    id: "ATOM_USDC",
    rujiraId: "GAIA.ATOM",
    title: "ATOM / USDC",
    description: "Atom",
    slug: MarketSlug.get("ATOM_USDC"),
    base: "ATOM",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: atomIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.ATOM/USD",
  },
  {
    id: "AVAX_USDC",
    rujiraId: "AVAX.AVAX",
    title: "AVAX / USDC",
    description: "Avalanche",
    slug: MarketSlug.get("AVAX_USDC"),
    base: "AVAX",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: avaxIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.AVAX/USD",
  },
  {
    id: "BTC_USDC",
    rujiraId: "BTC.BTC",
    title: "BTC / USDC",
    description: "Bitcoin",
    slug: MarketSlug.get("BTC_USDC"),
    base: "BTC",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: btcIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.BTC/USD",
  },
  {
    id: "BCH_USDC",
    rujiraId: "BCH.BCH",
    title: "BCH / USDC",
    description: "Bitcoin Cash",
    slug: MarketSlug.get("BCH_USDC"),
    base: "BCH",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: bchIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.BCH/USD",
  },
  {
    id: "BNB_USDC",
    rujiraId: "BSC.BNB",
    title: "BNB / USDC",
    description: "BNB",
    slug: MarketSlug.get("BNB_USDC"),
    base: "BNB",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: bnbIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.BNB/USD",
  },
  {
    id: "BTC_USDT",
    rujiraId: "BTC.BTC",
    title: "BTC / USDT",
    description: "Bitcoin",
    slug: MarketSlug.get("BTC_USDT"),
    base: "BTC",
    quote: "USDT",
    type: "collateral_is_quote",
    icon: btcIcon,
    collateralIcon: usdtIcon,
    benchmarksSymbol: "Crypto.BTC/USD",
  },
  {
    id: "DOGE_USDC",
    rujiraId: "DOGE.DOGE",
    title: "DOGE / USDC",
    description: "DOGE",
    slug: MarketSlug.get("DOGE_USDC"),
    base: "DOGE",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: dogeIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.DOGE/USD",
  },
  {
    id: "ETH_USDC",
    rujiraId: "ETH.ETH",
    title: "ETH / USDC",
    description: "Ethereum",
    slug: MarketSlug.get("ETH_USDC"),
    base: "ETH",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: ethIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.ETH/USD",
  },
  {
    id: "LTC_USDC",
    rujiraId: "LTC.LTC",
    title: "LTC / USDC",
    description: "Litecoin",
    slug: MarketSlug.get("LTC_USDC"),
    base: "LTC",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: ltcIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.LTC/USD",
  },
  {
    id: "RUNE_USDC",
    rujiraId: "THOR.RUNE",
    title: "RUNE / USDC",
    description: "THORChain",
    slug: MarketSlug.get("RUNE_USDC"),
    base: "RUNE",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: runeIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.RUNE/USD",
  },
  {
    id: "RUNE_USDT",
    rujiraId: "RUNE.RUNE",
    title: "RUNE / USDT",
    description: "THORChain",
    slug: MarketSlug.get("RUNE_USDT"),
    base: "RUNE",
    quote: "USDT",
    type: "collateral_is_quote",
    icon: runeIcon,
    collateralIcon: usdtIcon,
    benchmarksSymbol: "Crypto.RUNE/USD",
  },
  {
    id: "XRP_USDC",
    rujiraId: "XRP.XRP",
    title: "XRP / USDC",
    description: "Ripple",
    slug: MarketSlug.get("XRP_USDC"),
    base: "XRP",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: xrpIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.XRP/USD",
  },
  {
    id: "SOL_USDC",
    rujiraId: "SOL.SOL",
    title: "SOL / USDC",
    description: "Solana",
    slug: MarketSlug.get("SOL_USDC"),
    base: "SOL",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: solIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.SOL/USD",
  },
  {
    id: "TRX_USDC",
    rujiraId: "TRX.TRX",
    title: "TRX / USDC",
    description: "TRX",
    slug: MarketSlug.get("TRX_USDC"),
    base: "TRX",
    quote: "USDC",
    type: "collateral_is_quote",
    icon: trxIcon,
    collateralIcon: usdcIcon,
    benchmarksSymbol: "Crypto.TRX/USD",
  },
])

export const requireMarketDisplayConfig = ({
  marketId,
}: {
  marketId: MarketId
}): MarketDisplayConfig => {
  const config = marketDisplayConfigMap.get(marketId)
  if (config === undefined) {
    throw new Error(`Market display config not found for market ${marketId}`)
  }
  return config
}
